/**
 * 支払準備の画面の色分け。
 *
 * 作業の種類（実行・確認・freee）と口座（15日・りそな・みずほ）ごとに、既存のパステルの
 * トークン（styles.css）を割り当てる。色を足すのではなく、種類の見分けにだけ使う。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 支払準備のステップの種類と証憑整理フォルダの口座ごとに、札・丸・ボタンの色を決める
 * @graph-connects none
 */
import type { FOLDER_GROUPS, PaymentStepTone } from './payment-steps';

/** 作業の種類ごとの見た目 */
export interface ToneStyle {
  /** 札の文言 */
  label: string;
  /** 札（丸い小さなラベル）の指定 */
  chip: string;
  /** 番号の丸の指定 */
  dot: string;
}

/** @graph-connects none */
export const TONE_STYLES: Record<PaymentStepTone, ToneStyle> = {
  run: {
    label: '実行',
    chip: 'border-[var(--color-doing-border)] bg-[var(--color-doing-bg)] text-[var(--color-doing-text)]',
    dot: 'bg-[var(--color-doing-border)] text-[var(--color-bg-surface)]',
  },
  check: {
    label: '確認',
    chip: 'border-[var(--color-kind-closing)] bg-[var(--color-kind-closing-bg)] text-[var(--color-kind-closing-text)]',
    dot: 'bg-[var(--color-kind-closing)] text-[var(--color-bg-surface)]',
  },
  freee: {
    label: 'freee',
    chip: 'border-[var(--color-kind-meeting)] bg-[var(--color-kind-meeting-bg)] text-[var(--color-kind-meeting-text)]',
    dot: 'bg-[var(--color-kind-meeting)] text-[var(--color-bg-surface)]',
  },
};

/** 口座ごとの札の指定 */
/** @graph-connects none */
export const FOLDER_GROUP_CHIPS: Record<(typeof FOLDER_GROUPS)[number], string> = {
  '15日': 'bg-[var(--color-biz-ac-bg)] text-[var(--color-doing-text)]',
  りそな: 'bg-[var(--color-biz-salon-bg)] text-[var(--color-kind-meeting-text)]',
  みずほ: 'bg-[var(--color-biz-mall-bg)] text-[var(--color-kind-monthly-text)]',
  その他: 'bg-[var(--color-biz-common-bg)] text-[var(--color-kind-other-text)]',
};

/** 丸い主ボタン（GAS を動かす） */
/** @graph-connects none */
export const PRIMARY_PILL =
  'inline-flex shrink-0 items-center rounded-full bg-[var(--color-accent)] px-4 py-1.5 text-sm font-medium text-[var(--color-bg-surface)] shadow-[0_1px_2px_var(--color-shadow)] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40';

/** 丸い枠のボタン（開く・移動） */
/** @graph-connects none */
export const OUTLINE_PILL =
  'inline-flex shrink-0 items-center rounded-full border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-4 py-1.5 text-sm text-[var(--color-text-primary)] no-underline hover:bg-[var(--color-bg-subtle)]';

/** 小さな丸いボタン */
/** @graph-connects none */
export const SMALL_PILL =
  'shrink-0 rounded-full border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2.5 py-0.5 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]';

/** 角の丸いカード */
/** @graph-connects none */
export const ROUND_CARD =
  'rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] shadow-[0_1px_3px_var(--color-shadow)]';
